import {
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Portal,
  Skeleton,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  AdjustCurrentLiabilityTypesGQLType,
  useGetCreditFacilityLiabilityAccountsQuery,
  useAdjustCurrentLiabilityMutation,
  GetStatementDocumentGQLType,
} from '../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from '~/packages/common-frontend/colors';
import { Alert } from '~/packages/common-frontend/components/Alert';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Select } from '~/packages/common-frontend/components/Select';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { ColorScheme } from '~/packages/common-frontend/theme/types';

const AccountTypes = [
  'increaseCurrentLiability',
  'decreaseCurrentLiability',
] as const;

const FormValuesStruct = z.object({
  amount: z
    .number({
      message: 'Amount is required',
    })
    .positive({ message: 'Amount must be positive' }),

  currentCounterpartyLiabilityAccount: z.object(
    {
      tAccount: z.string(),
      organizationId: z.guid(),
    },
    {
      // required_error: 'Account is required',
      // invalid_type_error: 'Account is required',
      message: 'Account is required',
    },
  ),

  selectedDate: z.date({
    message: 'Date is required',
  }),

  type: z.enum(AccountTypes, { message: 'Adjustment type is required' }),
});
type FormValues = z.infer<typeof FormValuesStruct>;

export const AdjustCurrentLiabilitiesButton = ({
  creditFacilityId,
  minDate,
  maxDate,
  setIsTableLoading,
  buttonColorScheme,
  buttonText,
  buttonVariant = 'ghost',
}: {
  creditFacilityId: string;
  minDate: Date;
  maxDate: Date;
  setIsTableLoading: React.Dispatch<React.SetStateAction<boolean>>;
  buttonColorScheme?: ColorScheme;
  buttonText: string;
  buttonVariant?: string;
}) => {
  /**
   * ===========================
   * HOOKS
   * ===========================
   */
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const ref = React.useRef<HTMLElement>(null);

  /**
   * ===========================
   * FORM STATE
   * ===========================
   */

  const [amount, setAmount] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  const [
    currentCounterpartyLiabilityAccount,
    setCurrentCounterpartyLiabilityAccount,
  ] = useState<{
    tAccount: string;
    organizationId: string;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [type, setType] = useState<AdjustCurrentLiabilityTypesGQLType | null>(
    null,
  );
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  /**
   * ===========================
   * Error handling
   * ===========================
   */
  const [errors, setErrors] = useState<z.ZodIssue[]>([]);

  const amountErrors: z.ZodIssue[] = useMemo(() => {
    return errors.filter(({ path }) => path[0] === 'amount');
  }, [errors]);

  const currentCounterpartyLiabilityAccountErrors: z.ZodIssue[] = useMemo(() => {
    return errors.filter(
      ({ path }) => path[0] === 'currentCounterpartyLiabilityAccount',
    );
  }, [errors]);

  const typeErrors: z.ZodIssue[] = useMemo(() => {
    return errors.filter(({ path }) => path[0] === 'type');
  }, [errors]);

  const selectedDateErrors: z.ZodIssue[] = useMemo(() => {
    return errors.filter(({ path }) => path[0] === 'selectedDate');
  }, [errors]);

  /**
   * ===========================
   * DATA FETCH
   * ===========================
   */
  useEffect(() => {
    if (hasSubmitted) {
      const validationResults = FormValuesStruct.safeParse({
        amount,
        currentCounterpartyLiabilityAccount,
        selectedDate,
        type,
      });
      setErrors(
        validationResults.success ? [] : validationResults.error.issues,
      );
    }
  }, [
    hasSubmitted,
    amount,
    comment,
    currentCounterpartyLiabilityAccount,
    selectedDate,
    type,
  ]);

  const {
    data: creditFacilityData,
    loading: creditFacilityLoading,
  } = useGetCreditFacilityLiabilityAccountsQuery({
    variables: {
      creditFacilityId,
    },
  });

  const onCloseModal = useCallback(() => {
    setComment('');
    setAmount(0);
    setType(null);
    setSelectedDate(null);
    setCurrentCounterpartyLiabilityAccount(null);
    setHasSubmitted(false);
    setErrors([]);
    onClose();
  }, [onClose]);

  const [
    submitAdjustCurrentLiabilities,
    { loading: isLoading },
  ] = useAdjustCurrentLiabilityMutation({
    onError: (error) => {
      Sentry.captureException(error);
      toast({
        ...errorToastConfig,
        description:
          'Unable to adjust current liabilities. Please contact Finley support.',
      });
      setIsTableLoading(false);
    },
    onCompleted: () => {
      toast({
        title: 'Adjustment recorded',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
      setIsTableLoading(false);
      onCloseModal();
    },
    refetchQueries: [
      {
        query: GetStatementDocumentGQLType,
        variables: {
          creditFacilityId,
          startDate: commonDate.dateToFullDateString(minDate),
          endDate: commonDate.dateToFullDateString(maxDate),
        },
      },
    ],
  });

  const accountOptions = useMemo(() => {
    if (!creditFacilityData) {
      return [];
    }
    const { liabilityAccounts } = creditFacilityData.creditFacility;
    return liabilityAccounts.map(
      ({
        tAccount,
        organization: { id: organizationId, name: organizationName },
      }) => ({
        label: `${commonUtils.toTitleCase(tAccount)} for ${organizationName}`,
        value: {
          tAccount,
          organizationId,
        },
      }),
    );
  }, [creditFacilityData]);

  if (creditFacilityLoading || !creditFacilityData) {
    return <Skeleton height="40px" />;
  }

  // @jendevelops MSR-TODO: handle error state

  return (
    <>
      <Button
        onClick={onOpen}
        colorScheme={buttonColorScheme}
        variant={buttonVariant}
      >
        {buttonText}
      </Button>
      <Portal>
        <Modal finalFocusRef={ref} isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent p="32px">
            <ModalHeader>Adjust Liability Account</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing="16px" alignItems="flex-start">
                <Text>Adjustment</Text>
                <InputGroup w="100%">
                  <Select
                    placeholderLabel="Select Adjustment"
                    value={type}
                    onChange={(newValue) => {
                      if (newValue !== undefined) {
                        setType(newValue);
                      }
                    }}
                    options={[
                      {
                        label: 'Increase',
                        value: 'increaseCurrentLiability' as const,
                      },
                      {
                        label: 'Decrease',
                        value: 'decreaseCurrentLiability' as const,
                      },
                    ]}
                    isInvalid={
                      errors.filter(({ path }) => path[0] === 'type').length > 0
                    }
                  />
                </InputGroup>
                {typeErrors.map(({ message }) => (
                  <Alert status="error" key={message}>
                    <Text>{message}</Text>
                  </Alert>
                ))}

                {/* Account sets both the counterParty + the liability account */}
                <Text>Account</Text>
                <InputGroup w="100%">
                  <Select
                    placeholderLabel="Select Account"
                    value={currentCounterpartyLiabilityAccount}
                    onChange={(newValue) => {
                      if (newValue) {
                        setCurrentCounterpartyLiabilityAccount(newValue);
                      }
                    }}
                    options={accountOptions}
                    isInvalid={
                      currentCounterpartyLiabilityAccountErrors.length > 0
                    }
                  />
                </InputGroup>
                {currentCounterpartyLiabilityAccountErrors.map(
                  ({ message }) => (
                    <Alert status="error" key={message}>
                      <Text>{message}</Text>
                    </Alert>
                  ),
                )}

                <Text>Amount</Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon icon="dollar" />
                  </InputLeftElement>
                  <Input
                    placeholder="Amount"
                    type="number"
                    value={amount === null ? undefined : amount}
                    onChange={(e) => {
                      setAmount(parseFloat(e.target.value));
                    }}
                    isInvalid={amountErrors.length > 0}
                    _invalid={{
                      border: `1px solid ${colors.red[400]}`,
                      boxShadow: 'none',
                    }}
                  />
                </InputGroup>
                {amountErrors.map(({ message }) => (
                  <Alert status="error" key={message}>
                    <Text>{message}</Text>
                  </Alert>
                ))}

                <Text>Date</Text>
                <InputGroup w="100%">
                  <SimpleDatePicker
                    maxDate={maxDate}
                    minDate={minDate}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    dateFormat="MMMM d, yyyy"
                    placeHolder="Select date"
                    isInvalid={selectedDateErrors.length > 0}
                    width="100%"
                  />
                </InputGroup>
                {selectedDateErrors.map(({ message }) => (
                  <Alert status="error" key={message}>
                    <Text>{message}</Text>
                  </Alert>
                ))}
                <Text>Comment</Text>
                <Textarea
                  placeholder="Comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <HStack spacing="16px" w="100%" mt="16px">
                  <Button
                    colorScheme="aurora"
                    variant="outline"
                    onClick={onCloseModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="aurora"
                    variant="solid"
                    onClick={() => {
                      setHasSubmitted(true);

                      const validationResults = FormValuesStruct.safeParse({
                        amount,
                        currentCounterpartyLiabilityAccount,
                        selectedDate,
                        type,
                      });

                      if (!validationResults.success) {
                        setErrors(validationResults.error.issues);
                        return;
                      }

                      const validatedInputs: FormValues =
                        validationResults.data;

                      setIsTableLoading(true);
                      submitAdjustCurrentLiabilities({
                        variables: {
                          creditFacilityId,
                          amount: validatedInputs.amount,
                          comment: comment === '' ? null : comment,
                          counterpartyOrganizationId:
                            validatedInputs.currentCounterpartyLiabilityAccount
                              .organizationId ?? '',
                          currentLiabilityAccount:
                            currentCounterpartyLiabilityAccount?.tAccount ?? '',
                          date: commonDate.dateToFullDateString(
                            validatedInputs.selectedDate,
                          ),
                          type: validatedInputs.type,
                        },
                      });
                      setErrors([]);
                    }}
                    isLoading={isLoading}
                  >
                    Add Adjustment
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Portal>
    </>
  );
};
